import { useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { PRODUCT_NAME } from '../../content/provenance'
import { clearActivity } from '../../lib/inactivity'
import { hasSupabaseConfig, supabase } from '../../lib/supabase'
import { useTheme, type ThemePreference } from '../../theme/useTheme'
import { useTimeFormat, type TimeFormat } from '../../lib/timeFormat'
import { schedulingServiceEnabled } from '../../lib/features'
import { getMyNotifications, markNotificationRead, type AppNotification } from './notificationStore'

const WORKSPACES = ['agent', 'supervisor', 'admin', 'leadership']

const THEME_LABEL: Record<ThemePreference, string> = { light: 'Light', dark: 'Dark', system: 'System' }
const NEXT_THEME: Record<ThemePreference, ThemePreference> = { light: 'dark', dark: 'system', system: 'light' }

const TIME_FORMAT_LABEL: Record<TimeFormat, string> = { '24-hour': '24-hour', '12-hour': '12-hour' }
const NEXT_TIME_FORMAT: Record<TimeFormat, TimeFormat> = { '24-hour': '12-hour', '12-hour': '24-hour' }

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  )
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  )
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  )
}

// docs/design/journey-map.md's "each event with its time": a plain
// wall-clock timestamp, not a relative "3m ago" that would need to keep
// ticking while the panel stays open.
function formatNotificationTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

// Shared by both dropdowns in this header (the bell and the account
// menu): the WAI-ARIA menu pattern's own keyboard contract (Up/Down
// cycles the items, Escape closes and returns focus to the trigger, a
// pointerdown outside closes it too), identical for either one, so a
// fix here never has to land twice. Opening moves focus to the first
// item, per the same pattern.
function useMenuKeyboardNav(
  open: boolean,
  setOpen: (value: boolean) => void,
  containerRef: React.RefObject<HTMLElement | null>,
  menuRef: React.RefObject<HTMLElement | null>,
  triggerRef: React.RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return

    function menuItems() {
      return Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    }

    function onPointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
        return
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        const items = menuItems()
        if (items.length === 0) return
        const currentIndex = items.indexOf(document.activeElement as HTMLElement)
        const nextIndex =
          event.key === 'ArrowDown'
            ? (currentIndex + 1) % items.length
            : (currentIndex - 1 + items.length) % items.length
        items[nextIndex]?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)

    menuItems()[0]?.focus()

    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
}

/**
 * The chrome bar shared by every signed-in workspace: the wordmark, the
 * notification bell, and the account menu. The account menu carries the
 * theme control from docs/design/style-guide.md as a single item that
 * cycles Light, Dark, System on each activation, the time format control
 * (24-hour, this pilot's own data shape, or 12-hour) as another, and
 * sign-out, rather than a second and third control competing with the
 * bell and the wordmark for space in the bar itself.
 *
 * The bell reads the signed-in account's own real feed
 * (notificationStore.ts, 20260923000000_notifications.sql): unread count
 * on the dot and in the accessible name, newest first in the panel below
 * it, per docs/product/notifications.md's "each event with its time, a
 * one-line summary, and a link to the record." Opening a notification
 * (Enter/Space or a click) marks it read and navigates to its own link;
 * "Mark all as read" clears every unread one without navigating anywhere.
 * The header fetches its own session rather than taking a userId prop,
 * the same self-contained shape useTheme and useTimeFormat already have,
 * since fifteen call sites would otherwise all need to thread one through
 * just for this.
 *
 * Agent and supervisor share this header as-is; per-role primary
 * navigation, when there is more than one screen to navigate between,
 * is an addition to this component rather than a fork of it.
 */
export default function AppHeader({ initials }: { initials: string }) {
  const [accountOpen, setAccountOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const { preference, resolved, choose } = useTheme()
  const { format, setFormat } = useTimeFormat()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [role, setRole] = useState<string | null>(null)
  const accountMenuId = useId()
  const notificationsMenuId = useId()
  const accountContainerRef = useRef<HTMLDivElement>(null)
  const accountTriggerRef = useRef<HTMLButtonElement>(null)
  const accountMenuRef = useRef<HTMLDivElement>(null)
  const notificationsContainerRef = useRef<HTMLDivElement>(null)
  const notificationsTriggerRef = useRef<HTMLButtonElement>(null)
  const notificationsMenuRef = useRef<HTMLDivElement>(null)

  useMenuKeyboardNav(accountOpen, setAccountOpen, accountContainerRef, accountMenuRef, accountTriggerRef)
  useMenuKeyboardNav(notificationsOpen, setNotificationsOpen, notificationsContainerRef, notificationsMenuRef, notificationsTriggerRef)

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled || !session) return
      const loaded = await getMyNotifications()
      if (!cancelled) setNotifications(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // The wordmark leads back to the workspace's dashboard: the workspace the
  // page belongs to, or, on a page every role shares, the signed-in role's own.
  const section = pathname.split('/')[1]
  const pageWorkspace = WORKSPACES.includes(section) ? section : null
  useEffect(() => {
    if (pageWorkspace) return
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled || !session) return
      const { data: profile } = await supabase.from('profiles').select('role').eq('id', session.user.id).single()
      if (!cancelled) setRole(profile?.role ?? null)
    })
    return () => {
      cancelled = true
    }
  }, [pageWorkspace])
  const roleWorkspace = role === 'administrator' ? 'admin' : role
  const homeWorkspace = pageWorkspace ?? (roleWorkspace && WORKSPACES.includes(roleWorkspace) ? roleWorkspace : null)

  const unreadCount = notifications.filter((notification) => !notification.readAt).length
  const hasUnread = unreadCount > 0

  async function openNotification(notification: AppNotification) {
    setNotificationsOpen(false)
    if (!notification.readAt) {
      setNotifications((current) =>
        current.map((item) => (item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item)),
      )
      await markNotificationRead(notification.id)
    }
    navigate(notification.link)
  }

  async function markAllRead() {
    const unread = notifications.filter((notification) => !notification.readAt)
    if (unread.length === 0) return
    const readAt = new Date().toISOString()
    setNotifications((current) => current.map((item) => ({ ...item, readAt: item.readAt ?? readAt })))
    await Promise.all(unread.map((notification) => markNotificationRead(notification.id)))
  }

  async function handleLogout() {
    setAccountOpen(false)
    clearActivity()
    if (hasSupabaseConfig) await supabase.auth.signOut()
    navigate('/')
  }

  return (
    <header className="relative flex flex-none items-center justify-between bg-chrome px-4 py-3.5 text-chrome-ink sm:px-8">
      {homeWorkspace ? (
        <Link to={`/${homeWorkspace}`} aria-label={`${PRODUCT_NAME} dashboard`} className="rounded-control text-h4 font-extrabold tracking-tight hover:underline">
          {PRODUCT_NAME}
        </Link>
      ) : (
        <span className="text-h4 font-extrabold tracking-tight">{PRODUCT_NAME}</span>
      )}

      <div className="flex items-center gap-5">
        <div ref={notificationsContainerRef} className="relative">
          <button
            ref={notificationsTriggerRef}
            type="button"
            aria-label={hasUnread ? `Notifications, ${unreadCount} unread` : 'Notifications'}
            aria-haspopup="menu"
            aria-expanded={notificationsOpen}
            aria-controls={notificationsMenuId}
            onClick={() => setNotificationsOpen((value) => !value)}
            className="flex size-11 items-center justify-center text-chrome-ink"
          >
            <span className="relative flex">
              <BellIcon />
              {hasUnread && <span className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full border border-chrome bg-focus" />}
            </span>
          </button>

          {notificationsOpen && (
            <div
              id={notificationsMenuId}
              className="absolute top-full right-0 z-10 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-surface border border-line bg-canvas text-ink shadow-lg"
            >
              <div className="flex items-center justify-between gap-3 border-b border-line px-3.5 py-2.5">
                <span className="text-small font-semibold">Notifications</span>
                {hasUnread && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    className="text-caption font-medium text-primary hover:underline"
                  >
                    Mark all as read
                  </button>
                )}
              </div>

              {notifications.length === 0 ? (
                <p className="px-3.5 py-4 text-small text-ink-subtle">No notifications yet.</p>
              ) : (
                <div ref={notificationsMenuRef} role="menu" aria-label="Notifications" className="max-h-96 overflow-y-auto py-1">
                  {notifications.map((notification) => (
                    <button
                      key={notification.id}
                      type="button"
                      role="menuitem"
                      onClick={() => openNotification(notification)}
                      className="flex w-full items-start gap-2.5 px-3.5 py-3 text-left text-small hover:bg-surface"
                    >
                      <span
                        className={`mt-1.5 size-1.5 flex-none rounded-full ${notification.readAt ? 'bg-transparent' : 'bg-focus'}`}
                        aria-hidden="true"
                      />
                      <span className="flex-1">
                        <span className="block">{notification.summary}</span>
                        <span className="block text-caption text-ink-subtle">{formatNotificationTime(notification.createdAt)}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div ref={accountContainerRef} className="relative">
          <button
            ref={accountTriggerRef}
            type="button"
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            aria-controls={accountMenuId}
            onClick={() => setAccountOpen((value) => !value)}
            className="flex size-11 items-center justify-center rounded-full"
          >
            <span className="flex size-7 items-center justify-center rounded-full bg-chrome-ink-muted text-caption font-bold text-chrome">
              {initials}
            </span>
          </button>

          {accountOpen && (
            <div
              ref={accountMenuRef}
              id={accountMenuId}
              role="menu"
              aria-label="Account"
              className="absolute top-full right-0 z-10 mt-2 w-52 overflow-hidden rounded-surface border border-line bg-canvas py-1 text-ink shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => choose(NEXT_THEME[preference])}
                className="flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left text-small hover:bg-surface"
              >
                <span className="flex items-center gap-2.5">
                  {resolved === 'dark' ? <MoonIcon /> : <SunIcon />}
                  Theme
                </span>
                <span className="text-ink-subtle">{THEME_LABEL[preference]}</span>
              </button>

              <button
                type="button"
                role="menuitem"
                onClick={() => setFormat(NEXT_TIME_FORMAT[format])}
                className="flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left text-small hover:bg-surface"
              >
                <span className="flex items-center gap-2.5">
                  <ClockIcon />
                  Time format
                </span>
                <span className="text-ink-subtle">{TIME_FORMAT_LABEL[format]}</span>
              </button>

              {schedulingServiceEnabled() && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAccountOpen(false)
                    navigate('/account/notifications')
                  }}
                  className="flex w-full items-center gap-2.5 px-3.5 py-3 text-left text-small hover:bg-surface"
                >
                  <BellIcon />
                  Notification settings
                </button>
              )}

              <div role="separator" className="my-1 border-t border-line" />

              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex w-full items-center gap-2.5 px-3.5 py-3 text-left text-small hover:bg-surface"
              >
                <LogoutIcon />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
